import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import type {
  SetupIntent,
  StripeError,
  StripeElementChangeEvent,
  StripeElementType,
} from '@stripe/stripe-js'

import { alpha } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { TextField } from '@mui/material'
import { useFormContext } from 'react-hook-form'

import { StackItem } from 'ui/layout/StackItem'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import placeholder from 'config/placeholder'
import StripeInput from './StripeInput'

const useStyles = makeStyles()((theme: Theme) => ({
  input: {
    minWidth: theme.spacing(21),
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  },
  isValid: {
    backgroundColor: theme.palette.background.paper,
  },
  fullWidth: {
    width: '100%',
  },
}))

export type StripeUSerProps = {
  name?: string
  email?: string
}

export type StripeCardInputsProps = {
  posted: boolean
  onError: (error: StripeErrorProps | undefined) => void
  onSuccess: (result: any) => void
  secretKey?: string
  onChangeInput?: () => void
}

export type StripeErrorProps = {
  code?: string
  message: string
  type: SetupIntent['status'] | string
}

const StripeCardInputs: React.FC<StripeCardInputsProps> = ({
  posted,
  onError,
  onSuccess,
  secretKey,
}: StripeCardInputsProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const stripe = useStripe()
  const elements = useElements()

  const {
    watch,
    control,
    setError,
    trigger,
    setValue,
    formState: { errors },
  } = useFormContext()

  const watchEmail = watch('applicant_email')
  const watchName = watch('credit_name')

  const [succeeded, setSucceeded] = useState<boolean>(false)

  const [user, setUser] = useState<StripeUSerProps>({})
  const [validStripeElements, setValidStripeElements] = useState<{
    [key in StripeElementType]?: boolean
  }>({})

  const setupIndentError: (error: StripeError) => StripeErrorProps = (
    error
  ) => {
    let message = t(
      'errors.クレジットカード情報登録でエラーが発生しました。内容をご確認ください。'
    )
    const type = error.setup_intent?.status || 'stripe_api_error'
    // NOTE: ここで起こり得るエラー
    // https://stripe.com/docs/api/setup_intents/object#setup_intent_object-status
    if (error.setup_intent && error.setup_intent?.status === 'succeeded') {
      message = t('errors.このカードはすでに登録されています。')
    }
    if (error.setup_intent && error.setup_intent?.status === 'canceled') {
      message = t('errors.このカードは使用できません。')
    }
    return { type, message }
  }

  const postStripe = async () => {
    if (!stripe || !elements) {
      return
    }
    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement || !secretKey || !user) return

    const { error, setupIntent } = await stripe.confirmCardSetup(secretKey, {
      payment_method: {
        card: cardNumberElement,
        billing_details: user,
      },
    })
    if (error) {
      const { type, message } = setupIndentError(error)
      onError({
        type,
        message,
      })
    } else {
      setSucceeded(true)
      onSuccess(setupIntent)
    }
  }

  if (posted && !succeeded) {
    postStripe()
  }

  useEffect(() => {
    // NOTE: 顧客情報のチェック 登録済みのカードに問題などあればここでエラーが出る
    async function f() {
      if (stripe && secretKey) {
        const {
          error,
          // setupIntent
        } = await stripe.retrieveSetupIntent(secretKey)
        if (error) {
          const { type, message } = setupIndentError(error)
          onError({
            type,
            message,
          })
        }
      }
    }
    f()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, secretKey])

  useEffect(() => {
    setUser({ email: watchEmail, name: watchName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchName, watchEmail])

  useEffect(() => {
    setValue(
      'stripe_is_valid',
      !!(
        !errors?.cardCvc &&
        !errors?.cardExpiry &&
        !errors?.cardNumber &&
        validStripeElements?.cardCvc &&
        validStripeElements?.cardExpiry &&
        validStripeElements?.cardNumber
      ),
      { shouldValidate: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validStripeElements])

  const onStripeElementChange = (e: any) => {
    const { error, elementType, empty } = e as StripeElementChangeEvent
    if (error) {
      setError(elementType, { type: error.code, message: error.message })
    } else {
      // NOTE: hook-formのrequiredの挙動をシミュレート
      if (empty) {
        setError(elementType, {
          type: 'required',
          message: t('validation.入力してください'),
        })
      } else {
        trigger(elementType)
      }
    }
    setValidStripeElements((prev) => ({ ...prev, [elementType]: !empty }))
  }

  return (
    <div>
      <StackItem label={t('カード番号')} required>
        <TextField
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardNumberElement,
              className: cx(
                classes.input,
                classes.fullWidth,
                validStripeElements.cardNumber && classes.isValid
              ),
              onChange: onStripeElementChange,
            },
          }}
          variant="outlined"
          error={!!errors?.cardNumber}
          helperText={errors?.cardNumber?.message}
          fullWidth
        />
      </StackItem>
      <StackItem label={t('有効期限')} required>
        <TextField
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardExpiryElement,
              className: cx(
                classes.input,
                validStripeElements.cardExpiry && classes.isValid
              ),
              onChange: onStripeElementChange,
            },
          }}
          variant="outlined"
          error={!!errors?.cardExpiry}
          helperText={errors?.cardExpiry?.message}
        />
      </StackItem>
      <StackItemTextField
        required
        name={'credit_name'}
        label={t('名義')}
        placeholder={placeholder().credit_name}
        errorMessage={errors?.credit_name?.message}
        control={control}
      />
      <StackItem label={t('セキュリティコード')} required>
        <TextField
          InputProps={{
            inputComponent: StripeInput,
            inputProps: {
              component: CardCvcElement,
              className: cx(
                classes.input,
                validStripeElements.cardCvc && classes.isValid
              ),
              onChange: onStripeElementChange,
            },
          }}
          variant="outlined"
          error={!!errors?.cardCvc}
          helperText={errors?.cardCvc?.message}
        />
      </StackItem>
    </div>
  )
}

export default StripeCardInputs
